import { Phone, MapPin, ShieldCheck, Zap, House, BatteryCharging } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: ShieldCheck,
    title: 'Your certifications do the talking',
    text: 'Customers letting someone rewire their home want proof you’re qualified. Your site puts your own registrations — NICEIC, NAPIT, Part P, whatever you hold — front and centre, exactly where nervous customers look for them.',
  },
  {
    icon: MapPin,
    title: 'You show up for “electrician near me”',
    text: 'A complete Google Business Profile with the right categories, service areas and photos is what puts a local electrician on the map. We set it up properly and guide you through Google’s verification.',
  },
  {
    icon: Phone,
    title: 'Faults and emergencies call you first',
    text: 'When the power’s out, customers tap the first credible number they find. Click-to-call on every page, thumb-height on mobile, means that number is yours.',
  },
];

const WORK_TYPES = [
  {
    icon: Zap,
    title: 'Consumer unit upgrades',
    text: 'A job customers research carefully before booking. A clear services page explaining what’s involved — and your certifications beside it — wins the comparison.',
  },
  {
    icon: House,
    title: 'Rewires and renovations',
    text: 'Big-ticket, weeks-in-the-planning projects. A gallery of finished rewires and real reviews turns cautious homeowners into enquiries.',
  },
  {
    icon: BatteryCharging,
    title: 'EV chargers and modern installs',
    text: 'A fast-growing market where customers search first. A dedicated section on EV charger installation catches demand your competitors’ outdated sites miss.',
  },
];

const ELECTRICIAN_FAQS = [
  {
    q: 'Can the website show my NICEIC or NAPIT registration?',
    a: 'Yes — your own certifications and registrations are among the most persuasive things on your site, so we display them prominently wherever you hold them. We never imply endorsements or partnerships that don’t exist; we simply make your real credentials easy for customers to see.',
  },
  {
    q: 'Most of my work comes from builders and referrals. Why bother online?',
    a: 'Referrals get checked online before they call. When a builder or past customer passes your name on, the first thing that person does is search for you. A professional site makes that search confirm the recommendation — and picks up the growing number of customers who search first and ask nobody.',
  },
  {
    q: 'I do domestic and commercial work. Can the site cover both?',
    a: 'Yes. Your services are laid out so each kind of customer finds what applies to them — domestic call-outs and rewires on one hand, commercial testing, maintenance and installations on the other. The Built to Grow package gives you up to ten pages, plenty of room for both.',
  },
  {
    q: 'I’m flat out on the tools. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, some photos of your work from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function ElectriciansPage() {
  return (
    <>
      <Seo
        title="Websites for Electricians — Win Better Local Work | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK electricians. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-electricians"
        image={IMAGES.electrical.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="electricians-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for electricians
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              Qualified work deserves a website that <span className="text-lime">proves it.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything an electrician needs to be found and trusted locally: a fast,
              mobile-friendly website that showcases your certifications, a complete Google Business Profile and
              branded social pages — for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="electricians-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="electricians-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.electrical.url}
              alt={IMAGES.electrical.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What an electrician's online presence needs */}
      <section className="bg-offwhite" data-testid="electricians-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how electrical work is won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What an electrician’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="electricians-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  The jobs worth winning
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to attract the work you actually want
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  Electrical work ranges from ten-minute fixes to full rewires. Your website speaks to each customer
                  differently — and quietly filters for the better-paying, better-planned jobs.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.electricalPanel.url}
                  alt={IMAGES.electricalPanel.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="electricians-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit an electrical business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Most electricians choose Look Professional; if you want your branding sorted at the same time,
              Built to Grow includes a tailored logo and identity.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Electrician FAQs */}
      <section className="bg-offwhite" data-testid="electricians-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions electricians ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy electricians
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={ELECTRICIAN_FAQS} testid="electricians-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="electricians-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready to be the electrician people <span className="text-lime">trust first?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your electrical business, your certifications and the areas you cover — we’ll
                recommend the right starting point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="electricians-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
